import { Checkbox, Space } from 'antd'
import { LineItem, Action as LineItemAction, ActionType as LineItemActionType } from './lineItemsModel'
import { useJobItems } from '../Items/itemQueries'
import { ItemClass, ItemClasses, itemClassName } from '../../common/constants'
import VSpace from '../common/VSpace'
import './BulkActions.css'

type Props = {
  jobId: number
  lineItems: LineItem[]
  dispatchLineItem: (id: number) => (action: LineItemAction) => void
}

const BulkActions = ({jobId, lineItems, dispatchLineItem}: Props) => {
  const jobItems = useJobItems(jobId)
  const serverState = jobItems.data ?? { estimatedItems: [], catalogItems: [] }

  const allEnabled = lineItems.every(x => x.enabled)
  const someEnabled = lineItems.some(x => x.enabled)
  const allLocked = lineItems.every(x => x.locked)
  const someLocked = lineItems.some(x => x.locked)
  const allShowingItems = lineItems.every(x => x.showItems)
  const someShowingItems = lineItems.some(x => x.showItems)

  const enable = () => {
    lineItems.forEach(lineItem => dispatchLineItem(lineItem.id)({ type: LineItemActionType.EnabledChanged, value: !allEnabled }))
  }

  const lock = () => {
    lineItems.forEach(lineItem => dispatchLineItem(lineItem.id)({ type: LineItemActionType.LockedChanged, value: !allLocked }))
  }

  const itemize = () => {
    lineItems.forEach(lineItem => dispatchLineItem(lineItem.id)({ type: LineItemActionType.ShowItemsChanged, value: !allShowingItems, serverState }))
  }

  const toggleItemColumn = (column: string, enabled: boolean) => {
    lineItems.forEach(lineItem => dispatchLineItem(lineItem.id)({ type: LineItemActionType.ItemColumnToggled, value: column, enabled }))
  }

  const columnCheckbox = (column: string, label: string) => {
    const allSelected = lineItems.every(x => x.selectedItemColumns.includes(column))
    const someSelected = lineItems.some(x => x.selectedItemColumns.includes(column))
    return <Checkbox
      checked={allSelected}
      indeterminate={!allSelected && someSelected}
      onChange={() => toggleItemColumn(column, !allSelected)}
    >
      {label}
    </Checkbox>
  }


  const toggleItemClass = (classId: ItemClass, checked: boolean) => {
    lineItems.forEach(lineItem => dispatchLineItem(lineItem.id)({ type: LineItemActionType.ItemClassesChanged, classId, checked, serverState }))
  }

  const classCheckbox = (classId: ItemClass) => {
    const estimatedItemIds = lineItems.filter(x => x.enabled).flatMap(lineItem => {
      return serverState.estimatedItems
        .filter(x => lineItem.workareaIds.includes(x.workareaId))
        .filter(x => x.class === classId)
        .map(x => x.guid)
    })
    const allSelectedItemIds = lineItems.filter(x => x.enabled).flatMap(x => x.selectedItemGuids)
    const allSelected = estimatedItemIds.length > 0 && estimatedItemIds.every(x => allSelectedItemIds.includes(x))
    const someSelected = estimatedItemIds.some(x => allSelectedItemIds.includes(x))
    return <Checkbox
      checked={allSelected}
      indeterminate={!allSelected && someSelected}
      disabled={estimatedItemIds.length === 0}
      onChange={() => toggleItemClass(classId, !allSelected)}
    >
      {itemClassName(classId)}
    </Checkbox>
  }

  return <VSpace className="bulk-actions">
    <Space size="large" style={{alignItems: 'flex-start'}}>
      <Checkbox checked={allEnabled} indeterminate={!allEnabled && someEnabled} onChange={enable}>Show All</Checkbox>
      <Checkbox checked={allLocked} indeterminate={!allLocked && someLocked} onChange={lock}>Lock All</Checkbox>
      <Checkbox checked={allShowingItems} indeterminate={!allShowingItems && someShowingItems} onChange={itemize}>Show Items on All</Checkbox>
      {someShowingItems && <>
        <VSpace>
          {columnCheckbox('quantityAndUnits', 'Quantity & Units')}
          {columnCheckbox('totalPrice', 'Total Price')}
        </VSpace>
        <VSpace>
          {classCheckbox(ItemClasses.Labor)}
          {classCheckbox(ItemClasses.Materials)}
          {classCheckbox(ItemClasses.Equipment)}
          {classCheckbox(ItemClasses.Subcontracting)}
        </VSpace>
      </>}
    </Space>
  </VSpace>
}

export default BulkActions